import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export interface ChecklistItem {
  _id?: string;
  label: string;
  hint?: string;
  required?: boolean;
}
export interface ProjectSubType {
  _id?: string;
  name: string;
  active: boolean;
  checklist: ChecklistItem[];
}
export interface ProjectTypeConfig {
  _id: string;
  name: string;
  active: boolean;
  order: number;
  checklist: ChecklistItem[];
  subTypes: ProjectSubType[];
}

interface State {
  types: ProjectTypeConfig[];
  loading: boolean;
  fetchTypes: (all?: boolean) => Promise<void>;
  createType: (body: Partial<ProjectTypeConfig>) => Promise<boolean>;
  updateType: (id: string, body: Partial<ProjectTypeConfig>) => Promise<boolean>;
  setActive: (id: string, active: boolean) => Promise<void>;
}

export const useProjectTypeStore = create<State>((set, get) => ({
  types: [],
  loading: false,
  fetchTypes: async (all = false) => {
    set({ loading: true });
    try {
      const { data } = await axiosInstance.get("/project-types", { params: all ? { all: 1 } : {} });
      set({ types: data.types || [] });
    } catch {
      // silent — pickers fall back to empty
    } finally {
      set({ loading: false });
    }
  },
  createType: async (body) => {
    try {
      await axiosInstance.post("/project-types", body);
      toast.success("Project type created");
      await get().fetchTypes(true);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to create");
      return false;
    }
  },
  updateType: async (id, body) => {
    try {
      await axiosInstance.put(`/project-types/${id}`, body);
      toast.success("Saved");
      await get().fetchTypes(true);
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to save");
      return false;
    }
  },
  setActive: async (id, active) => {
    try {
      await axiosInstance.patch(`/project-types/${id}/active`, { active });
      toast.success(active ? "Activated" : "Archived");
      await get().fetchTypes(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Failed to update");
    }
  },
}));
